import _ from 'underscore'
import React from 'react'
import moment from 'moment'

import AppConstants from './AppConstants'
import Details from '../components/elements/Details'
import SetStatusAction from '../actions/app/SetStatusAction'

const AppUtils = {

  // Reference to the Redux store
  store: null,

  // Extract a title from a standard { status, code, message } error object
  errorToTitle(error) {
    return `Erreur ${error.status}`
  },

  // Extract a descriptive text from a standard { status, code, message } error object
  errorToText(error, customMessages = {}) {
    return (
      error.status === AppConstants.ERROR.S_400_BAD_REQUEST
      ||
      error.status === AppConstants.ERROR.S_500_SERVER_EXCEPTION
    )
      ? (
          _.has(customMessages, error.code)
            ? customMessages[error.code]
            : `${error.code}: ${error.message}`
        )
      : (
          error.status === AppConstants.ERROR.S_401_UNAUTHORIZED
            ? 'Connexion requise pour accéder à l\'application'
            : (
                error.status === AppConstants.ERROR.S_403_FORBIDDEN
                  ? 'Accès refusé. Vous ne disposez pas des droits nécessaires.'
                  : 'Erreur inconnue'
              )
        )
  },

  // Change app status
  // type is from AppConstants.STATUS.TYPE.*
  setStatus(type, message) {
    if (!_.isNull(this.store)) {
      this.store.dispatch(SetStatusAction({
        type,
        message
      }))
    }
  },

  // Display a date timestamp of the form YYYY-MM-DD in readable format
  displayDate(jsonDate, ifNull='-', includeSpan=true) {
    if (_.isNull(jsonDate)) {
      return ifNull
    }
    const parsedDate = jsonDate.split('-')
    const result = `${parsedDate[2]}.${parsedDate[1]}.${parsedDate[0]}`
    return (
      includeSpan
        ? <span>
            {result}
          </span>
        : result

    )
  },

  // Display a periodicity
  displayPeriodicity(periodicity) {
    const period = periodicity % 12 === 0
      ? `${periodicity/12} an${(periodicity/12) === 1 ? '' : 's'}`
      : `${periodicity} mois`
    return `Tous les ${period}`
  },

  // Determine the time remaining before a given date
  // @return { quantity, unit }
  remainingPeriod(jsonDate) {
    if (_.isNull(jsonDate)) {
      return null
    }
    const now = moment().startOf('day')
    const soon = moment(jsonDate)
    let quantity = soon.diff(now, 'years')
    let unit = `an${Math.abs(quantity) > 1 ? 's' : ''}`
    if (Math.abs(quantity) < 1) {
      quantity = soon.diff(now, 'months')
      unit = 'mois'
    }
    if (Math.abs(quantity) < 1) {
      quantity = soon.diff(now, 'weeks')
      unit = `semaine${Math.abs(quantity) > 1 ? 's' : ''}`
    }
    if (Math.abs(quantity) < 1) {
      quantity = soon.diff(now, 'days')
      unit = `jour${Math.abs(quantity) > 1 ? 's' : ''}`
    }
    if (Math.abs(quantity) < 1) {
      unit = "aujourd'hui"
    }
    return { quantity, unit }
  },

  // Build a details icon for a given date (maintenance)
  // @return { text, icon, details }
  dateToReminder(jsonDate) {
    const remaining = this.remainingPeriod(jsonDate)
    // If null, no date provided
    if (_.isNull(remaining)) {
      return {
        text: 'Au plus vite',
        icon: Details.ICON.FAILURE,
        details: 'Jamais effectuée'
      }
    }
    if (remaining.quantity < 0) {
      return {
        text: this.displayDate(jsonDate),
        icon: Details.ICON.FAILURE,
        details: `${Math.abs(remaining.quantity)} ${remaining.unit} de retard`
      }
    }
    if (remaining.quantity === 0) {
      return {
        text: this.displayDate(jsonDate),
        icon: Details.ICON.WARNING,
        details: "Aujourd'hui"
      }
    }
    return {
      text: this.displayDate(jsonDate),
      icon: (
        remaining.unit.includes('jour')
        ||
        remaining.unit.includes('semaine')
      )
        ? Details.ICON.WARNING
        : null,
      details: (
        remaining.unit.includes('jour')
        ||
        remaining.unit.includes('semaine')
      )
        ? `Dans ${remaining.quantity} ${remaining.unit}`
        : null
    }
  },

  // Select the worst details icon among a given set
  worstIcon(icons) {
    if (!_.isArray(icons)) {
      return null
    }
    const iconToNumber = (icon) => {
      switch (icon) {
        case Details.ICON.INFO:
          return 1
        case Details.ICON.SUCCESS:
          return 2
        case Details.ICON.WARNING:
          return 3
        case Details.ICON.FAILURE:
          return 4
        default:
          return 0
      }
    }
    return _.reduce(
      icons,
      (worst, icon) => (
        iconToNumber(icon) > iconToNumber(worst)
          ? icon
          : worst
      ),
      null
    )
  },

  getDetailsInventory(item) {
    let icon, content
    switch (item.inventory.status) {
      case AppConstants.PRODUCT.ITEM.INVENTORY.STATUS.NONE:
        icon = Details.ICON.WARNING
        content = 'Jamais inventorié'
        break
      case AppConstants.PRODUCT.ITEM.INVENTORY.STATUS.PRESENT:
        icon = Details.ICON.SUCCESS
        content = `Inventorié le ${this.displayDate(item.inventory.date, '-', false)}`
        break
      case AppConstants.PRODUCT.ITEM.INVENTORY.STATUS.MISSING:
        icon = Details.ICON.FAILURE
        content = `Manquant dans le dernier inventaire du ${this.displayDate(item.inventory.date, '-', false)}`
        break
      default:
        icon = Details.ICON.INFO
        content = 'Status inconnu'
        break
    }
    return { icon, content }
  },

  // Perform a string-search
  search(base, search) {
    const normalize = (str) => (str.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase())
    return normalize(base).includes(normalize(search))
  },

  // Access nested objects properties based on string
  accessByString(o, s) {
    s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
    s = s.replace(/^\./, '');           // strip a leading dot
    var a = s.split('.');
    for (var i = 0, n = a.length; i < n; ++i) {
        var k = a[i];
        if (k in o) {
            o = o[k];
        } else {
            return;
        }
    }
    return o;
  }
}

export default AppUtils



// WEBPACK FOOTER //
// ./src/utils/AppUtils.js